import React, {useState} from 'react';

import PropTypes from 'prop-types';
import CSSModules from 'react-css-modules';
import {useIntl} from 'react-intl';
import ReactTooltip from 'react-tooltip';

import {
  AlertIcon,
  Button,
  CrossIcon,
  Dropdown,
  Link,
  RefreshIcon,
  SearchIcon,
  SearchInput,
  SortIcon,
  Text,
} from '@edume/magnificent';

import {sortOptions as sortOptionTypes} from '../../../utils/people.js';

import styles from './styles.module.scss';

const FilterBar = ({
  // Core filtering props
  totalItems,
  visibleItems,
  searchValue,
  setSearchValue,
  sortBy,
  setSortBy,

  // Localization keys
  itemType, // 'teams', 'courses', etc.

  // Optional error/warning counts
  itemsWithWarningsCount = 0,
  itemsWithErrorsCount = 0,

  // Optional localStorage key for sort preference
  sortStorageKey,
}) => {
  const intl = useIntl();
  const [searchVisible, setSearchVisible] = useState(false);

  const capitaliseItemType = (type) =>
    type.charAt(0).toUpperCase() + type.slice(1);

  const sortOptions = [
    {
      title: intl.formatMessage({id: `Sort.option.mostRecent`}),
      callback: () => {
        setSortBy(sortOptionTypes.mostRecent);
        if (sortStorageKey) {
          localStorage.setItem(sortStorageKey, 'false');
        }
      },
      type: sortOptionTypes.mostRecent,
    },
    {
      title: intl.formatMessage({id: `Sort.option.aToZ`}),
      callback: () => {
        setSortBy(sortOptionTypes.aToZ);
        if (sortStorageKey) {
          localStorage.setItem(sortStorageKey, 'true');
        }
      },
      type: sortOptionTypes.aToZ,
    },
  ];

  const renderErrorsAndWarnings = () => {
    if (!itemsWithErrorsCount && !itemsWithWarningsCount) {
      return null;
    }

    return (
      <div styleName='items-with-error-count'>
        {itemsWithErrorsCount ? (
          <>
            <AlertIcon colour={'errorPrimary'} />
            <Text
              size='m'
              weight={'semiBold'}
              colour={'errorPrimary'}
              inline={true}
            >
              {intl.formatMessage(
                {id: `Learning.${itemType}.errorCount`},
                {n: itemsWithErrorsCount}
              )}
            </Text>
          </>
        ) : (
          <>
            <AlertIcon colour={'grey700'} />
            <Text
              size='m'
              weight={'medium'}
              colour={'textSecondary'}
              inline={true}
            >
              {intl.formatMessage(
                {id: `Learning.${itemType}.errorCount`},
                {n: itemsWithWarningsCount}
              )}
            </Text>
          </>
        )}
      </div>
    );
  };

  return (
    <div styleName='container'>
      <div styleName='count'>
        <div styleName='item-count'>
          <Text size='m' weight='medium'>
            {intl.formatMessage(
              {
                id: `${capitaliseItemType(itemType)}.filter.count`,
              },
              {
                [`visible${capitaliseItemType(itemType)}`]: visibleItems,
                [`total${capitaliseItemType(itemType)}`]: totalItems,
                searchValue,
              }
            )}
          </Text>
          {searchValue && (
            <Link
              onClick={() => setSearchValue(null)}
              text={intl.formatMessage({id: 'Filter.showAll'})}
              dataAutomation='reset-search-link'
              icon={
                <RefreshIcon colour='primary' direction='left' size='tiny' />
              }
            />
          )}
        </div>
        {renderErrorsAndWarnings()}
      </div>
      {!searchVisible ? (
        <div styleName='buttonContents'>
          <Dropdown
            type='thirdary'
            title={intl.formatMessage({id: 'Sort.title'})}
            options={sortOptions}
            changeOptionText={true}
            selectedIndex={sortOptions.findIndex(
              (option) => option.type === sortBy
            )}
            defaultText={intl.formatMessage({id: 'Sort.defaultText'})}
            buttonProps={{
              icon: <SortIcon colour='grey700' />,
            }}
          />
          <ReactTooltip
            id='searchTip'
            type='dark'
            effect='solid'
            className='overlay-tooltip arrow-right'
            place='top'
          >
            {intl.formatMessage({
              id: `${capitaliseItemType(itemType)}.Search.searchTooltip`,
            })}
          </ReactTooltip>
          <div data-tip data-for='searchTip' styleName='search-button'>
            <Button
              type='thirdary'
              dataAutomation='search-icon-button'
              onClick={() => setSearchVisible(true)}
              icon={<SearchIcon colour='grey700' />}
              noMarginLeft
              noMarginTop
              accessibleText={intl.formatMessage({
                id: `${capitaliseItemType(itemType)}.Search`,
              })}
            />
          </div>
        </div>
      ) : (
        <div styleName='search-container'>
          <SearchInput
            dropdownOptions={[]}
            onFreeTextSearch={(searchTerm) => {
              setSearchValue(searchTerm.toLowerCase().trim());
              setSearchVisible(false);
            }}
            dataAutomation={`${itemType}-search-input`}
            clearSearchOnSelect
            placeholder={intl.formatMessage({
              id: `${capitaliseItemType(itemType)}.Search.placeholder`,
            })}
            autoFocus
            clearInputType='none'
          />
          <Button
            type='thirdary'
            dataAutomation='close-search-icon-button'
            onClick={() => setSearchVisible(false)}
            icon={<CrossIcon colour='grey700' />}
            noBorder
            noMarginLeft
            noMarginTop
          />
        </div>
      )}
    </div>
  );
};

FilterBar.propTypes = {
  // Core filtering props
  totalItems: PropTypes.number.isRequired,
  visibleItems: PropTypes.number.isRequired,
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  setSortBy: PropTypes.func.isRequired,

  // Localization
  itemType: PropTypes.string.isRequired, // 'teams', 'courses', etc.

  // Optional props
  itemsWithWarningsCount: PropTypes.number,
  itemsWithErrorsCount: PropTypes.number,
  sortStorageKey: PropTypes.string,
};

export default CSSModules(FilterBar, styles);
